import { useState, useEffect } from 'react';
import { Layout } from '@/components/ui/Layout';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { ResourceImage } from '@/components/ui/ResourceImage';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import {
  FileText,
  Newspaper,
  Headphones,
  Youtube,
  Globe,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  ExternalLink,
  Filter
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface LibraryResource {
  id: string;
  title: string;
  description: string;
  url: string;
  type: 'pdf' | 'article' | 'podcast' | 'video' | 'web';
  created_at: string;
  thumbnail_url: string | null;
}

const RESOURCE_TYPES = [
  { value: 'all', label: 'All Resources', icon: Filter },
  { value: 'pdf', label: 'PDFs', icon: FileText },
  { value: 'article', label: 'Articles', icon: Newspaper },
  { value: 'podcast', label: 'Podcasts', icon: Headphones },
  { value: 'video', label: 'Videos', icon: Youtube },
  { value: 'web', label: 'Web Resources', icon: Globe },
];

export function Library() {
  const { user } = useAuth();
  const [resources, setResources] = useState<LibraryResource[]>([]);
  const [filteredResources, setFilteredResources] = useState<LibraryResource[]>([]);
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState<LibraryResource | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    type: 'article' as LibraryResource['type'],
    thumbnail_url: ''
  });
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.email?.endsWith('@admin.com');

  useEffect(() => {
    loadResources();
  }, []);

  useEffect(() => {
    filterResources();
  }, [selectedType, resources]);

  async function loadResources() {
    try {
      const { data, error } = await supabase
        .from('library_resources')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setResources(data || []);
      setFilteredResources(data || []);
    } catch (err) {
      console.error('Error loading resources:', err);
      setError('Failed to load resources');
    } finally {
      setLoading(false);
    }
  }

  function filterResources() {
    if (selectedType === 'all') {
      setFilteredResources(resources);
    } else {
      setFilteredResources(resources.filter(resource => resource.type === selectedType));
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (editingResource) {
        const { error } = await supabase
          .from('library_resources')
          .update(formData)
          .eq('id', editingResource.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('library_resources')
          .insert([{
            ...formData,
            created_by: user?.id
          }]);

        if (error) throw error;
      }

      await loadResources();
      setShowModal(false);
      setEditingResource(null);
      setFormData({
        title: '',
        description: '',
        url: '',
        type: 'article',
        thumbnail_url: ''
      });
    } catch (err) {
      console.error('Error saving resource:', err);
      setError('Failed to save resource');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (resource: LibraryResource) => {
    setEditingResource(resource);
    setFormData({
      title: resource.title,
      description: resource.description,
      url: resource.url,
      type: resource.type,
      thumbnail_url: resource.thumbnail_url || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this resource?')) return;

    try {
      const { error } = await supabase
        .from('library_resources')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadResources();
    } catch (err) {
      console.error('Error deleting resource:', err);
      setError('Failed to delete resource');
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Resource Library</h1>
          {isAdmin && (
            <Button onClick={() => setShowModal(true)}>
              <Plus className="h-5 w-5 mr-2" />
              Add Resource
            </Button>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6 p-4">
          <div className="flex flex-wrap gap-2">
            {RESOURCE_TYPES.map(type => {
              const Icon = type.icon;
              return (
                <button
                  key={type.value}
                  onClick={() => setSelectedType(type.value)}
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedType === type.value
                      ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {type.label}
                  {type.value !== 'all' && (
                    <span className="ml-2 bg-white dark:bg-gray-800 px-2 py-0.5 rounded-full text-xs">
                      {resources.filter(r => r.type === type.value).length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResources.map((resource, index) => (
            <div key={resource.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              <ResourceImage
                src={resource.thumbnail_url}
                alt={resource.title}
                type={resource.type}
                priority={index < 6}
              />
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    {resource.type === 'pdf' && <FileText className="h-5 w-5 text-red-500" />}
                    {resource.type === 'article' && <Newspaper className="h-5 w-5 text-blue-500" />}
                    {resource.type === 'podcast' && <Headphones className="h-5 w-5 text-purple-500" />}
                    {resource.type === 'video' && <Youtube className="h-5 w-5 text-red-600" />}
                    {resource.type === 'web' && <Globe className="h-5 w-5 text-green-500" />}
                    <span className="ml-2 text-sm font-medium text-gray-500 dark:text-gray-400 capitalize">
                      {resource.type}
                    </span>
                  </div>
                  {isAdmin && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(resource)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(resource.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2">
                  {resource.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  {resource.description}
                </p>
                <a
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  View Resource
                  <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Form Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                {editingResource ? 'Edit Resource' : 'Add Resource'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Title
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    URL
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as LibraryResource['type'] })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="pdf">PDF</option>
                    <option value="article">Article</option>
                    <option value="podcast">Podcast</option>
                    <option value="video">Video</option>
                    <option value="web">Web Resource</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Thumbnail URL (optional)
                  </label>
                  <input
                    type="url"
                    value={formData.thumbnail_url}
                    onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      setEditingResource(null);
                      setFormData({
                        title: '',
                        description: '',
                        url: '',
                        type: 'article',
                        thumbnail_url: ''
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}